import React, { FC, useEffect } from 'react';
import { Button, Col, Form, Input, Row } from 'antd';
import moment from 'moment';
import { DATE_FORMAT_WITH_TIME, formRules } from 'common/const/common.const';
import CustomDatePicker from 'common/components/DateTime/CustomDatePicker';
import { ImageUpload } from 'common/components/Image/ImageUpload';
import { useFormMapper } from 'common/helpers/form.helper';
import { dateFormatterToUTC } from 'common/helpers/date.helper';
import { EErrorStatus } from 'common/helpers/error.helper';
import { useIsContentRestricted } from 'common/hooks/useIsContentRestricted';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { setAnnouncementModelError, setAnnouncementModelParams } from 'app/store/reducers/announcement.reducer';
import { StoreMultipleSelector } from 'entities/Store/components/Selector/StoreMultipleSelector';
import {
  EAnnouncementStatus,
  IAnnouncementCreateData,
  IAnnouncementUpdateData,
} from 'entities/Announcement/Announcement.models';

interface IComponentProps {
  announcementId?: string;
  createAnnouncement?: (data: IAnnouncementCreateData) => void;
  editAnnouncement?: (data: IAnnouncementUpdateData) => void;
}

export const AnnouncementForm: FC<IComponentProps> = ({ announcementId, createAnnouncement, editAnnouncement }) => {
  const dispatch = useAppDispatch();

  const { announcementModel, announcementModelLoading, announcementModelParams, announcementModelError } =
    useAppSelector((state) => state.announcement);

  const { fields, setField } = useFormMapper(
    ['name', 'stores', 'href', 'description', 'image', 'promoCode'],
    announcementModel,
    announcementModelParams,
    announcementModelError
  );

  const isUpdateForm = !!announcementId;
  const submitButtonText = isUpdateForm ? 'Save' : ' Add';
  // Use the BE-computed status (single source of truth) instead of recomputing on the
  // client — keeps editability in sync with what the API actually enforces on update.
  const announcementStatus = announcementModel?.status ?? null;
  // Lock only when editing an already-live announcement; create is always editable.
  const canManageAnnouncement = !useIsContentRestricted();
  const isFormDisabled = (isUpdateForm && announcementStatus !== EAnnouncementStatus.Pending) || !canManageAnnouncement;

  const clearFormData = () => {
    dispatch(setAnnouncementModelParams(null));
    dispatch(setAnnouncementModelError(null));
  };

  useEffect(() => {
    return () => {
      clearFormData();
    };
  }, []);

  useEffect(() => {
    if (isUpdateForm && announcementModel?.startDate && announcementModel?.endDate) {
      setField({
        name: 'announcementDates',
        value: [moment.utc(announcementModel.startDate), moment.utc(announcementModel.endDate)],
      });
    }
  }, [announcementModel]);

  // It needs for mapping errors with separate date values to one datepicker control
  useEffect(() => {
    const dateError =
      announcementModelError?.data?.errors?.['.startDate'] || announcementModelError?.data?.errors?.['.endDate'];

    if (announcementModelError?.data?.statusCode === EErrorStatus.Validation) {
      if (dateError) {
        setField({ name: 'announcementDates', errors: dateError.map((it) => it.message) });
      }
    }
  }, [announcementModelError]);

  const onSubmit = (values: any) => {
    const { name, announcementDates, stores, href, description, image, promoCode } = values;
    const newData: IAnnouncementCreateData = {
      name,
      startDate: dateFormatterToUTC(announcementDates[0]),
      endDate: dateFormatterToUTC(announcementDates[1]),
      storeIds: stores?.map((item: any) => item.id),
      href: href?.length ? href : null,
      description,
      image,
      promoCode: promoCode?.length ? promoCode : null,
    };

    if (isUpdateForm) {
      editAnnouncement && editAnnouncement({ id: announcementId, ...newData });
    } else {
      createAnnouncement && createAnnouncement(newData);
    }
  };

  const onCancel = () => {
    clearFormData();

    history.go(-1);
  };

  return (
    <Form className="mt-5" onFinish={onSubmit} fields={fields} layout="vertical">
      <Row gutter={[28, 0]}>
        <Col span={12}>
          <Form.Item
            rules={[
              formRules.required,
              {
                min: 2,
                message: 'Announcement name must be longer than or equal 2 characters',
              },
              {
                max: 100,
                message: 'Announcement name must be shorter than or equal 30 characters',
              },
            ]}
            name="name"
            label="Announcement name"
          >
            <Input placeholder="Announcement name" name="name" type="text" disabled={isFormDisabled} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item rules={[formRules.required]} name="announcementDates" label="Announcement dates">
            <CustomDatePicker.RangePicker
              className="width-full"
              disabledDate={(current) => current && current < moment().startOf('day')}
              getPopupContainer={(triggerNode) => triggerNode as HTMLElement}
              format={DATE_FORMAT_WITH_TIME}
              showTime={true}
              disabled={isFormDisabled}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Store" name="stores">
            <StoreMultipleSelector disabled={isFormDisabled} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
            name="href"
            label="Announcement hyperlink"
          >
            <Input placeholder="Announcement hyperlink" name="href" type="text" disabled={isFormDisabled} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="promoCode" label="Promo code">
            <Input placeholder="Existing promo code" name="promoCode" type="text" disabled={isFormDisabled} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            rules={[
              formRules.required,
              {
                max: 75,
                message: 'Description must be shorter than or equal 75 characters',
              },
            ]}
            name="description"
            label="Description"
          >
            <Input.TextArea placeholder="Description" name="description" disabled={isFormDisabled} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item rules={[formRules.required]} name="image" label='Image (recommended dimensions: "700px x 610px")'>
        <ImageUpload disabled={isFormDisabled} targetWidth={700} targetHeight={610} />
      </Form.Item>

      <Row justify="space-between" align="middle">
        <Col span={16}>
          <Form.Item className="mb-0">
            <Button
              className="mr-8"
              htmlType="submit"
              type="primary"
              disabled={announcementModelLoading || isFormDisabled}
              loading={announcementModelLoading}
              title={submitButtonText}
            >
              {submitButtonText}
            </Button>
            <Button
              onClick={onCancel}
              disabled={announcementModelLoading}
              loading={announcementModelLoading}
              title="Cancel"
            >
              Cancel
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
